import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert, Badge, Button, Checkbox, CheckboxGroup, ChevronDown, ChevronUp, Grid01, Modal, Select, Table, TextInput,
} from "@opendate-app/setlist";
import { SeriesChangeList, SeriesNote } from "../../shared/components/SeriesChangeList";
import { formatDateTimeLong, formatOwnValue, ownLabel, ownLabels } from "../../../shared/seriesOwnValues";
import { formatCurrencySymbol } from "../../../shared/teamCurrencyFormatting";
import { errorText } from "../../../shared/toastHelper";
import {
  RESULT_COLORS, UPDATING_STATUSES, formatChangeValue, formatDateLong, pendingFileRows, plural, resultSummary, updateNotes, venueDay,
} from "../updateDates";

export const PREVIEW_DEBOUNCE_MS = 400;

const GROUP_LABELS = { add_ons: "Add-ons", promo_codes: "Promo codes" };
const CANT_PUSH = "Can't push. Edit these per date.";

const NoteParts = ({ parts }) => parts.map((part, index) =>
  (typeof part === "string" ? <React.Fragment key={index}>{part}</React.Fragment> : <strong key={index}>{part.strong}</strong>));

// The server takes a positive price for a new section (or a free section, set in Ticket Config).
export const sectionPriced = (price) => parseFloat(price) > 0;

const priceText = (price) => String(parseFloat(price));

// A new section starts at the price of a ticket type that matches it, or else
// the section listed before it. Each section is offered a price once, so one
// the user clears stays clear.
const usePrefilledSectionPrices = ({ chart, sectionPrice, onSectionPrice, sectionFree, matchingSectionPrice }) => {
  const offered = useRef(new Set());
  useEffect(() => {
    let previous = null;
    chart.sections.forEach((section) => {
      if (section.status === "removed") return;
      let price = section.status === "carries_over" ? section.price : sectionPrice(chart.show_type, section.key);
      if (section.status === "new" && !offered.current.has(section.key)) {
        offered.current.add(section.key);
        if (!sectionPriced(price) && !sectionFree(chart.show_type, section.key)) {
          const suggestion = [matchingSectionPrice(chart.show_type, section), previous].find(sectionPriced);
          if (suggestion) {
            price = priceText(suggestion);
            onSectionPrice(chart.show_type, section, price);
          }
        }
      }
      if (sectionPriced(price)) previous = price;
    });
  }, [chart.change_id, chart.sections.map((section) => `${section.status}:${section.key}`).join(",")]);
};

const ChartPanel = ({ chart, currency, sectionPrice, onSectionPrice, sectionFree, matchingSectionPrice }) => {
  const [touched, setTouched] = useState([]);
  usePrefilledSectionPrices({ chart, sectionPrice, onSectionPrice, sectionFree, matchingSectionPrice });
  const switching = chart.switching_dates;
  const keeping = chart.keeping_dates;
  const columns = [
    {
      key: "label",
      header: "Section on the new chart",
      render: (section) => (section.status === "removed"
        ? <span className="series-update-removed-section">{section.label}</span>
        : (
          <span className="series-update-section-name">
            {section.label}
            {section.status === "new" && <Badge color="brand" size="sm">New section</Badge>}
          </span>
        )),
    },
    {
      key: "seats",
      header: "Seats",
      render: (section) => {
        if (section.status === "removed") return "Not on the new chart. Its ticket type is removed from switching dates.";
        return section.seats_before !== null && section.seats_before !== undefined && section.seats_before !== section.seats_after
          ? `${section.seats_before} → ${section.seats_after}`
          : section.seats_after;
      },
    },
    {
      key: "price",
      header: "Price",
      render: (section) => {
        if (section.status === "removed") return null;
        if (section.status === "carries_over") {
          return (
            <span>
              {formatOwnValue(section.price, "money", { currency })}
              <span className="series-update-muted"> · carries over</span>
            </span>
          );
        }
        const value = sectionPrice(chart.show_type, section.key);
        return (
          <div className="series-update-price-input">
            <TextInput
              size="sm"
              leadingText={formatCurrencySymbol(currency)}
              inputMode="decimal"
              value={value}
              aria-label={`${section.label} price`}
              error={touched.includes(section.key) && !sectionPriced(value) ? "Required" : undefined}
              onBlur={() => setTouched((keys) => (keys.includes(section.key) ? keys : [...keys, section.key]))}
              onChange={(e) => onSectionPrice(chart.show_type, section, e.target.value)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="series-update-block">
      <div className="series-update-label">Seating chart</div>
      <Alert
        color="brand"
        icon={Grid01}
        title={`${switching} ${plural(switching, "date switches", "dates switch")} to ${chart.to.name}`}
        description={keeping > 0
          ? `${keeping} ${plural(keeping, "date has", "dates have")} sales and ${keeping === 1 ? "keeps" : "keep"} ${chart.from.name}. Sold seats can't move to a different chart, so ${keeping === 1 ? "that date keeps it as its" : "those dates keep it as their"} own setting.`
          : undefined}
      />
      <Table columns={columns} data={chart.sections} getRowKey={(section) => `${section.status}:${section.key}`} />
      {chart.holds.map((hold) => (hold.carries_over ? (
        <SeriesNote key={hold.name} tone="success">
          <strong>{hold.name}</strong>
          {` (${hold.seats} ${plural(hold.seats, "seat")}) moves to the same seats on ${chart.to.name}.`}
        </SeriesNote>
      ) : (
        <SeriesNote key={hold.name} tone="warning">
          <strong>{hold.name}</strong>
          {` (${hold.seats} ${plural(hold.seats, "seat")}) uses seats that aren't on ${chart.to.name}. It won't carry over to the switching dates, so re-create it on the new chart after you save.`}
        </SeriesNote>
      )))}
    </div>
  );
};

const UpdateDatesModal = ({
  seriesName, initialPreview, timeZone, currency, saving, fetchPreview, onConfirm, onCancel, sectionPrice, onSectionPrice,
  sectionFree = () => false, matchingSectionPrice = () => null, eventTypes = [], eventTemplates = [], pendingFiles, stale = false,
}) => {
  const [preview, setPreview] = useState(initialPreview);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [scope, setScope] = useState("all");
  const upcoming = initialPreview.dates.filter((date) => !date.past);
  // A multiple-shows series has several dates on one venue day; Starting lists the day once.
  const startingOptions = upcoming
    .map((date) => ({ value: venueDay(date.starts_at, timeZone), label: `${formatDateLong(date.starts_at, timeZone)} and later` }))
    .filter((option, index, options) => options.findIndex((other) => other.value === option.value) === index);
  const [from, setFrom] = useState(upcoming.length > 0 ? venueDay(upcoming[0].starts_at, timeZone) : "");
  const [selectedIds, setSelectedIds] = useState(() => upcoming.map((date) => date.event_id));
  const [ownValues, setOwnValues] = useState("keep");
  const [chosenIds, setChosenIds] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [retries, setRetries] = useState(0);
  const requestId = useRef(0);
  // initialPreview already answers the opening choices, unless the series
  // changed while it loaded.
  const armed = useRef(stale);

  const datesWithOwn = preview.dates.filter((date) => date.included && date.own_values.length > 0);
  const chosen = Object.fromEntries(preview.dates
    .filter((date) => date.included && chosenIds.includes(date.event_id))
    .map((date) => [date.event_id, date.own_values.map((own) => own.key)]));
  const params = { scope, from, selectedIds, ownValues, chosen };
  // Keyed on what's sent, not on the ticked ids: a date coming back into scope
  // adds itself to chosen, so the shown preview always answers what Update sends.
  const chosenKey = JSON.stringify(chosen);
  // A section's price lands on the series' ticket types, which the preview
  // reads, so a typed price refreshes it too.
  const priceKey = (preview.chart || []).flatMap((chart) => chart.sections
    .filter((section) => section.status === "new")
    .map((section) => `${chart.show_type}:${section.key}=${sectionPrice(chart.show_type, section.key)}`)).join(",");

  useEffect(() => {
    if (!armed.current) {
      armed.current = true;
      return undefined;
    }
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    const timer = setTimeout(() => {
      fetchPreview(params)
        .then((next) => {
          if (id !== requestId.current) return;
          setPreview(next);
          setPreviewError(null);
        })
        .catch((e) => {
          if (id === requestId.current) setPreviewError(errorText(e, "Couldn't check which dates update. Try again."));
        })
        .finally(() => { if (id === requestId.current) setLoading(false); });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [scope, from, selectedIds.join(","), ownValues, chosenKey, priceKey, retries]);

  const formatting = { preview, currency, timeZone, eventTypes, eventTemplates };
  const changeRows = [
    ...preview.changes.map((change) => ({
      key: change.id,
      label: change.label || change.id,
      groupLabel: GROUP_LABELS[change.group],
      ...(change.pushable
        ? { from: formatChangeValue(change, "before", formatting), to: formatChangeValue(change, "after", formatting) }
        : { note: CANT_PUSH }),
    })),
    ...pendingFileRows(pendingFiles),
  ];
  const notes = updateNotes(preview);
  const summary = preview.summary;
  const priceMissing = (preview.chart || []).some((chart) => chart.sections.some((section) =>
    section.status === "new" && !sectionFree(chart.show_type, section.key) && !sectionPriced(sectionPrice(chart.show_type, section.key))));
  const updating = summary.updating;
  const ownText = (own) => formatOwnValue(own.own, own.kind, { currency, timeZone });
  const seriesText = (own) => formatOwnValue(own.series, own.kind === "removed" ? "text" : own.kind, { currency, timeZone });
  const first = preview.changes[0];
  let confirmLabel = `Update ${updating} ${plural(updating, "date")}`;
  if (updating === 0) confirmLabel = "Save to series only";
  if (saving) confirmLabel = "Updating…";

  const toggleSelected = (eventId) => setSelectedIds((ids) =>
    (ids.includes(eventId) ? ids.filter((id) => id !== eventId) : [...ids, eventId]));

  const dateColumns = [
    { key: "date", header: "Date", render: (date) => formatDateLong(date.starts_at, timeZone) },
    { key: "result", header: "Result", render: (date) => <Badge color={RESULT_COLORS[date.status] || "gray"} size="sm">{date.result}</Badge> },
    {
      key: "detail",
      header: first ? first.label || first.id : "",
      render: (date) => {
        if (!first) return null;
        const kept = date.own_values.find((own) => own.kept && (own.change_ids || []).includes(first.id));
        if (kept) return ownText(kept);
        if (date.status === "not_affected") return "—";
        return UPDATING_STATUSES.includes(date.status)
          ? `${formatChangeValue(first, "before", formatting)} → ${formatChangeValue(first, "after", formatting)}`
          : formatChangeValue(first, "before", formatting);
      },
    },
  ];

  return (
    <Modal
      open
      onClose={saving ? () => {} : onCancel}
      icon={false}
      width={680}
      title={`Update dates in ${seriesName}?`}
      description={`This series is published with ${summary.total_dates} ${plural(summary.total_dates, "date")}. Choose which dates get these changes.`}
      footer={(
        <div className="series-update-footer">
          <span className="series-update-summary" data-testid="update-dates-summary">
            {priceMissing ? "Price the new sections to continue" : resultSummary(summary)}
          </span>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>Back to editing</Button>
          <Button
            variant="primary"
            disabled={saving || loading || Boolean(previewError) || priceMissing}
            onClick={() => onConfirm(params)}
          >
            {confirmLabel}
          </Button>
        </div>
      )}
    >
      <div className="series-update-body">
        {previewError && (
          <Alert
            color="error"
            title={previewError}
            actions={<Button variant="link-color" size="sm" disabled={loading} onClick={() => setRetries((count) => count + 1)}>Try again</Button>}
          />
        )}
        <div className="series-update-block">
          <div className="series-update-label">{`${changeRows.length} ${plural(changeRows.length, "change")}`}</div>
          <SeriesChangeList rows={changeRows} />
        </div>

        <div className="series-update-block">
          <div className="series-update-label" id="series-update-scope-label">Apply to</div>
          <CheckboxGroup
            type="radio"
            aria-labelledby="series-update-scope-label"
            value={scope}
            onChange={setScope}
            items={[
              {
                value: "all",
                label: `All upcoming dates (${summary.upcoming_dates})`,
                supportingText: summary.past_dates > 0
                  ? `${summary.past_dates} past ${plural(summary.past_dates, "date")} stay as they were.`
                  : "Every date that hasn't happened yet.",
              },
              { value: "from", label: "Dates from a specific date", supportingText: "For a change partway through the run." },
              { value: "selected", label: "Selected dates", supportingText: "Pick exactly which dates change." },
            ]}
          />
          {scope === "from" && (
            <div className="series-update-scope-detail">
              <Select
                label="Starting"
                value={from}
                onChange={(value) => setFrom(value)}
                options={startingOptions}
              />
            </div>
          )}
          {scope === "selected" && (
            <div className="series-update-date-list">
              {upcoming.map((listed) => preview.dates.find((entry) => entry.event_id === listed.event_id) || listed).map((date) => (
                <div key={date.event_id} className="series-update-date-row" data-testid={`update-date-${date.event_id}`}>
                  <Checkbox
                    size="sm"
                    checked={selectedIds.includes(date.event_id)}
                    onChange={() => toggleSelected(date.event_id)}
                    label={formatDateTimeLong(date.starts_at, timeZone)}
                  />
                  <span className="series-update-spacer" />
                  {date.own_values.length > 0 && (
                    <Badge color="warning" size="sm">{`Own: ${ownLabels(date.own_values).join(", ")}`}</Badge>
                  )}
                  {date.sold_count > 0 && <span className="series-update-muted">{`${date.sold_count.toLocaleString()} sold`}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {(preview.chart || []).map((chart) => (
          <ChartPanel
            key={chart.change_id}
            chart={chart}
            currency={currency}
            sectionPrice={sectionPrice}
            onSectionPrice={onSectionPrice}
            sectionFree={sectionFree}
            matchingSectionPrice={matchingSectionPrice}
          />
        ))}

        {datesWithOwn.length > 0 && (
          <div className="series-update-block">
            <Alert
              color="warning"
              title={`${datesWithOwn.length} ${plural(datesWithOwn.length, "date has its", "dates have their")} own ${plural(datesWithOwn.length, "value")} for what you changed`}
              description={datesWithOwn
                .map((date) => `${formatDateLong(date.starts_at, timeZone)}: ${date.own_values.map((own) => (own.kind === "extra_row" ? ownLabel(own) : `${ownLabel(own)} ${ownText(own)}`)).join(", ")}`)
                .join(" · ")}
            />
            <CheckboxGroup
              type="radio"
              aria-label="Dates with their own values"
              value={ownValues}
              onChange={setOwnValues}
              items={[
                { value: "keep", label: "Keep their own values", supportingText: "Recommended. Someone changed these on purpose." },
                { value: "update", label: "Update them too", supportingText: "Their own values are cleared and they follow the series again." },
                { value: "choose", label: "Let me choose", supportingText: "Pick which ones update." },
              ]}
            />
            {ownValues === "choose" && (
              <CheckboxGroup
                aria-label="Dates whose own values update"
                values={chosenIds}
                onChange={setChosenIds}
                items={datesWithOwn.map((date) => ({
                  value: date.event_id,
                  label: formatDateLong(date.starts_at, timeZone),
                  supportingText: date.own_values.map((own) => `${ownText(own)} → ${seriesText(own)}`).join(", "),
                }))}
              />
            )}
          </div>
        )}

        {notes.tickets.length > 0 && (
          <div className="series-update-block" data-testid="ticket-notes">
            <div className="series-update-label">What happens to tickets</div>
            {notes.tickets.map((note) => <SeriesNote key={note.key} tone={note.tone}><NoteParts parts={note.parts} /></SeriesNote>)}
          </div>
        )}

        {notes.extras.length > 0 && (
          <div className="series-update-block" data-testid="extra-notes">
            <div className="series-update-label">What happens to add-ons and promo codes</div>
            {notes.extras.map((note) => <SeriesNote key={note.key} tone={note.tone}><NoteParts parts={note.parts} /></SeriesNote>)}
          </div>
        )}

        {notes.settings.length > 0 && (
          <div className="series-update-block" data-testid="setting-notes">
            <div className="series-update-label">What happens to date settings</div>
            {notes.settings.map((note) => <SeriesNote key={note.key} tone={note.tone}><NoteParts parts={note.parts} /></SeriesNote>)}
          </div>
        )}

        <div>
          <Button
            variant="link-color"
            size="sm"
            icon={showPreview ? ChevronUp : ChevronDown}
            aria-expanded={showPreview}
            onClick={() => setShowPreview((open) => !open)}
          >
            Preview every date
          </Button>
          {showPreview && (
            <div className="series-update-preview" data-testid="update-dates-preview">
              <Table columns={dateColumns} data={preview.dates} getRowKey={(date) => date.event_id} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

UpdateDatesModal.propTypes = {
  seriesName: PropTypes.string.isRequired,
  initialPreview: PropTypes.shape({
    changes: PropTypes.array.isRequired,
    dates: PropTypes.array.isRequired,
    notes: PropTypes.object,
    chart: PropTypes.array,
    summary: PropTypes.object.isRequired,
  }).isRequired,
  timeZone: PropTypes.string,
  currency: PropTypes.string,
  saving: PropTypes.bool,
  fetchPreview: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  sectionPrice: PropTypes.func.isRequired,
  onSectionPrice: PropTypes.func.isRequired,
  sectionFree: PropTypes.func,
  matchingSectionPrice: PropTypes.func,
  eventTypes: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.any, label: PropTypes.node })),
  eventTemplates: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.any, label: PropTypes.node })),
  stale: PropTypes.bool,
  pendingFiles: PropTypes.shape({
    photo: PropTypes.object,
    sponsorLogos: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, file: PropTypes.object })),
    addOnImages: PropTypes.arrayOf(PropTypes.shape({ clientKey: PropTypes.string, name: PropTypes.string })),
  }),
};

export default UpdateDatesModal;
